/**
 * PwaInstallCard — Crown & Rose web-reader PWA install card.
 *
 * Install flow:
 *   1. Chrome / Edge / Android Chrome → native beforeinstallprompt dialog
 *   2. iOS Safari                     → InstallGuideSheet (Share → Add to Home Screen)
 *   3. iOS Chrome / Firefox / Brave   → InstallGuideSheet (redirect to Safari first)
 *   4. Android Firefox / Samsung      → InstallGuideSheet (browser-menu steps)
 *   5. macOS Safari ≥17               → InstallGuideSheet (File → Add to Dock)
 *   6. Desktop Firefox                → InstallGuideSheet (switch to Chrome / Edge)
 *   7. Anything else                  → InstallGuideSheet (generic copy-link steps)
 *
 * The InstallGuideSheet:
 *   • Does NOT close on backdrop click — stays open so users can follow along
 *   • Step-by-step navigation (Back / Next step / Done)
 *   • Progress bar across the top
 *   • Visual hint per step showing exactly what UI element to find
 *
 * Card is hidden when already running standalone or dismissed within 14 days.
 */
import React, { useState, useEffect, useRef } from 'react';
import { X, Clipboard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { triggerReaderInstall } from '@/lib/readerPwaInstall';

const KIT = {
  icons: {
    ios:      '/reader/icons/apple-touch-icon.png?v=3',
    android:  '/reader/icons/icon-192.png?v=3',
    maskable: '/reader/icons/maskable-192.png?v=3',
    large:    '/reader/icons/icon-512.png?v=3',
  },
  brand: {
    primary:    '#F7A04B',
    on_primary: '#2B2321',
    surface:    '#F5EFEA',
    text:       '#2B2321',
    muted:      '#8C7D79',
    border:     '#EBE1DA',
    bg_step:    '#FFF9F4',
    bg_hint:    '#F3F4F6',
    hint_border:'#E5E7EB',
  },
  copy: {
    card_title:    'Install The Indie Reader on any device',
    card_subtitle: 'One tap opens your library, full-screen like an app.',
    button_label:  'Install now',
    why_title:     'Why we do it this way',
    why_paragraphs: [
      'We offer our service this way so that more of the sales revenue goes to the authors who create your favorite stories instead of the platforms that take a large percentage of sales for their platform.',
      'We want to be a direct-to-reader platform, lower cost to the user, and more revenue for the author.',
    ],
    privacy_popup: {
      button_label: 'Your Privacy',
      title:        'Your Privacy',
      text:         'Our business model is you, our reader. We keep anonymous information in-house and never share it with any other party. We only wish to provide the reader with the best possible experience across all of their devices. Your privacy is paramount to Crown & Rose. Please enjoy the platform worry-free.',
    },
  },
};

const DISMISS_KEY    = 'cr_install_dismissed_at';
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000;

function isDismissed() {
  try { const ts = Number(localStorage.getItem(DISMISS_KEY)); return ts > 0 && Date.now() - ts < DISMISS_TTL_MS; } catch { return false; }
}
function saveDismiss() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}
function isStandalone() {
  try { return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true; } catch { return false; }
}

function detectPlatform() {
  const ua = navigator.userAgent || '';
  const ios     = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const android = /Android/i.test(ua);
  const mac     = /Macintosh/i.test(ua) && !ios;

  const isChrome  = /Chrome\/\d/i.test(ua)   && !/Edg\/\d/i.test(ua);
  const isEdge    = /Edg\/\d/i.test(ua);
  const isFirefox = /Firefox\/\d/i.test(ua);
  const isSamsung = /SamsungBrowser\/\d/i.test(ua);
  const isBrave   = typeof navigator.brave !== 'undefined';
  const isSafari  = /^((?!chrome|crios|android|fxios).)*safari/i.test(ua);
  const isCriOS   = /CriOS/i.test(ua);
  const isFxiOS   = /FxiOS/i.test(ua);

  const safariVerMatch = ua.match(/Version\/(\d+)/);
  const safariVersion  = safariVerMatch ? parseInt(safariVerMatch[1], 10) : 0;

  return {
    ios, android, mac,
    mobile: ios || android,
    iosSafari:      ios && isSafari,
    iosOther:       ios && (isCriOS || isFxiOS || (!isSafari && !isCriOS && !isFxiOS)),
    androidChrome:  android && isChrome,
    androidSamsung: android && isSamsung,
    androidFirefox: android && isFirefox,
    androidOther:   android && !isChrome && !isSamsung && !isFirefox,
    macSafari:      mac && isSafari,
    macSafariNew:   mac && isSafari && safariVersion >= 17,
    desktopChrome:  !ios && !android && isChrome && !isEdge && !isBrave,
    desktopEdge:    !ios && !android && isEdge,
    desktopFirefox: !ios && !android && isFirefox,
    desktopBrave:   !ios && !android && isBrave,
    desktopOther:   !ios && !android && !isChrome && !isEdge && !isFirefox,
  };
}

// ─── Browser-specific install guide data ────────────────────────────────────

function copyURL() {
  try { navigator.clipboard.writeText(window.location.href); } catch { /* silent */ }
}

function getInstallGuide(platform, t) {
  const T = (k, opts) => t(`pwaInstall.${k}`, opts);
  if (platform.iosSafari) {
    return {
      title:       T('iosSafari.title'),
      browserName: T('iosSafari.browserName'),
      hint:        T('iosSafari.hint'),
      steps: [
        { title: T('iosSafari.step1Title'), desc: T('iosSafari.step1Desc'), visual: 'ios-share' },
        { title: T('iosSafari.step2Title'), desc: T('iosSafari.step2Desc'), visual: 'add-home' },
        { title: T('iosSafari.step3Title'), desc: T('iosSafari.step3Desc'), visual: 'add-confirm' },
      ],
    };
  }
  if (platform.iosOther) {
    return {
      title:       T('iosOther.title'),
      browserName: T('iosOther.browserName'),
      hint:        T('iosOther.hint'),
      steps: [
        { title: T('iosOther.step1Title'), desc: T('iosOther.step1Desc'), visual: 'copy-link', action: { label: T('guide.copyLink'), fn: copyURL } },
        { title: T('iosOther.step2Title'), desc: T('iosOther.step2Desc'), visual: 'open-safari' },
        { title: T('iosOther.step3Title'), desc: T('iosOther.step3Desc'), visual: 'ios-share' },
        { title: T('iosOther.step4Title'), desc: T('iosOther.step4Desc'), visual: 'add-home' },
      ],
    };
  }
  if (platform.androidChrome) {
    return {
      title:       T('androidChrome.title'),
      browserName: T('androidChrome.browserName'),
      hint:        T('androidChrome.hint'),
      steps: [
        { title: T('androidChrome.step1Title'), desc: T('androidChrome.step1Desc'), visual: 'android-menu' },
        { title: T('androidChrome.step2Title'), desc: T('androidChrome.step2Desc'), visual: 'menu-install' },
        { title: T('androidChrome.step3Title'), desc: T('androidChrome.step3Desc'), visual: 'add-confirm' },
      ],
    };
  }
  if (platform.androidSamsung) {
    return {
      title:       T('androidSamsung.title'),
      browserName: T('androidSamsung.browserName'),
      hint:        T('androidSamsung.hint'),
      steps: [
        { title: T('androidSamsung.step1Title'), desc: T('androidSamsung.step1Desc'), visual: 'android-menu' },
        { title: T('androidSamsung.step2Title'), desc: T('androidSamsung.step2Desc'), visual: 'menu-install' },
        { title: T('androidSamsung.step3Title'), desc: T('androidSamsung.step3Desc'), visual: 'add-confirm' },
      ],
    };
  }
  if (platform.androidFirefox || platform.androidOther) {
    const ns = platform.androidFirefox ? 'androidFirefox' : 'androidOther';
    return {
      title:       T(`${ns}.title`),
      browserName: T(`${ns}.browserName`),
      hint:        T(`${ns}.hint`),
      steps: [
        { title: T(`${ns}.step1Title`), desc: T(`${ns}.step1Desc`), visual: 'android-menu' },
        { title: T(`${ns}.step2Title`), desc: T(`${ns}.step2Desc`), visual: 'menu-install' },
        { title: T(`${ns}.step3Title`), desc: T(`${ns}.step3Desc`), visual: 'add-confirm' },
      ],
    };
  }
  if (platform.macSafariNew) {
    return {
      title:       T('macSafariNew.title'),
      browserName: T('macSafariNew.browserName'),
      hint:        T('macSafariNew.hint'),
      steps: [
        { title: T('macSafariNew.step1Title'), desc: T('macSafariNew.step1Desc'), visual: 'mac-menu' },
        { title: T('macSafariNew.step2Title'), desc: T('macSafariNew.step2Desc'), visual: 'mac-add-dock' },
        { title: T('macSafariNew.step3Title'), desc: T('macSafariNew.step3Desc'), visual: 'add-confirm' },
      ],
    };
  }
  if (platform.macSafari) {
    return {
      title:       T('macSafari.title'),
      browserName: T('macSafari.browserName'),
      hint:        T('macSafari.hint'),
      steps: [
        { title: T('macSafari.step1Title'), desc: T('macSafari.step1Desc'), visual: 'copy-link', action: { label: T('guide.copyLink'), fn: copyURL } },
        { title: T('macSafari.step2Title'), desc: T('macSafari.step2Desc'), visual: 'open-browser' },
        { title: T('macSafari.step3Title'), desc: T('macSafari.step3Desc'), visual: 'desktop-install-icon' },
      ],
    };
  }
  if (platform.desktopFirefox) {
    return {
      title:       T('desktopFirefox.title'),
      browserName: T('desktopFirefox.browserName'),
      hint:        T('desktopFirefox.hint'),
      steps: [
        { title: T('desktopFirefox.step1Title'), desc: T('desktopFirefox.step1Desc'), visual: 'copy-link', action: { label: T('guide.copyLink'), fn: copyURL } },
        { title: T('desktopFirefox.step2Title'), desc: T('desktopFirefox.step2Desc'), visual: 'open-browser' },
        { title: T('desktopFirefox.step3Title'), desc: T('desktopFirefox.step3Desc'), visual: 'desktop-install-icon' },
      ],
    };
  }
  if (platform.desktopBrave || platform.desktopChrome || platform.desktopEdge) {
    const name = platform.desktopEdge ? T('desktopChrome.browserNameEdge') : platform.desktopBrave ? T('desktopChrome.browserNameBrave') : T('desktopChrome.browserNameChrome');
    return {
      title:       T('desktopChrome.title'),
      browserName: name,
      hint:        T('desktopChrome.hint'),
      steps: [
        { title: T('desktopChrome.step1Title'), desc: T('desktopChrome.step1Desc'), visual: 'desktop-install-icon' },
        {
          title: T('desktopChrome.step2Title'),
          desc:  platform.desktopEdge ? T('desktopChrome.step2DescEdge') : T('desktopChrome.step2DescOther'),
          visual: 'desktop-menu',
        },
        { title: T('desktopChrome.step3Title'), desc: T('desktopChrome.step3Desc'), visual: 'add-confirm' },
      ],
    };
  }
  // Generic fallback
  return {
    title:       T('generic.title'),
    browserName: T('generic.browserName'),
    hint:        T('generic.hint'),
    steps: [
      { title: T('generic.step1Title'), desc: T('generic.step1Desc'), visual: 'copy-link', action: { label: T('guide.copyLink'), fn: copyURL } },
      { title: T('generic.step2Title'), desc: T('generic.step2Desc'), visual: 'desktop-install-icon' },
      { title: T('generic.step3Title'), desc: T('generic.step3Desc'), visual: 'add-confirm' },
    ],
  };
}

// ─── Step visual hints ────────────────────────────────────────────────────────

function VisualHint({ type, t }) {
  const b = KIT.brand;
  const wrap = {
    backgroundColor: b.bg_hint,
    border: `1px solid ${b.hint_border}`,
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 16px',
    gap: 7,
    minHeight: 76,
  };
  const lbl = {
    fontSize: 10.5, color: '#6B7280', textAlign: 'center',
    fontFamily: "'DM Sans', system-ui, sans-serif", lineHeight: 1.4,
  };

  if (type === 'ios-share') return (
    <div style={wrap}>
      <svg width="38" height="38" viewBox="0 0 38 38" fill="none">
        <rect x="1" y="1" width="36" height="36" rx="8" stroke="#E5E7EB" strokeWidth="1.5" fill="white"/>
        <path d="M19 10v14" stroke={b.primary} strokeWidth="2.5" strokeLinecap="round"/>
        <path d="M13.5 15l5.5-5.5 5.5 5.5" stroke={b.primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M11 22v8h16v-8" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span style={lbl}>{t('pwaInstall.visual.shareBtn')}</span>
    </div>
  );

  if (type === 'add-home') return (
    <div style={wrap}>
      <svg width="120" height="34" viewBox="0 0 120 34" fill="none">
        <rect x="0.5" y="0.5" width="119" height="33" rx="6" stroke="#E5E7EB" fill="white"/>
        <text x="10" y="21" fontSize="11" fill="#374151" fontFamily="system-ui">Add to Home Screen</text>
        <rect x="98" y="9" width="14" height="14" rx="3.5" fill={b.primary}/>
        <text x="102.5" y="20" fontSize="13" fill="white" fontFamily="system-ui" fontWeight="bold">+</text>
      </svg>
      <span style={lbl}>{t('pwaInstall.visual.scrollSheet')}</span>
    </div>
  );

  if (type === 'add-confirm') return (
    <div style={wrap}>
      <svg width="110" height="32" viewBox="0 0 110 32" fill="none">
        <rect x="0.5" y="0.5" width="109" height="31" rx="6" stroke="#E5E7EB" fill="white"/>
        <text x="10" y="20" fontSize="11" fill="#374151" fontFamily="system-ui">The Indie Reader</text>
        <rect x="82" y="7" width="20" height="17" rx="4" fill={b.primary}/>
        <text x="85.5" y="19.5" fontSize="10" fill="white" fontFamily="system-ui" fontWeight="bold">Add</text>
      </svg>
      <span style={lbl}>{t('pwaInstall.visual.tapAdd')}</span>
    </div>
  );

  if (type === 'android-menu') return (
    <div style={wrap}>
      <svg width="30" height="38" viewBox="0 0 30 38" fill="none">
        <rect x="0.5" y="0.5" width="29" height="37" rx="6" stroke="#E5E7EB" fill="white"/>
        <circle cx="15" cy="11" r="2.5" fill="#6B7280"/>
        <circle cx="15" cy="19" r="2.5" fill="#6B7280"/>
        <circle cx="15" cy="27" r="2.5" fill="#6B7280"/>
      </svg>
      <span style={lbl}>{t('pwaInstall.visual.menuTopRight')}</span>
    </div>
  );

  if (type === 'menu-install') return (
    <div style={wrap}>
      <svg width="130" height="40" viewBox="0 0 130 40" fill="none">
        <rect x="0.5" y="0.5" width="129" height="39" rx="6" stroke="#E5E7EB" fill="white"/>
        <rect x="8" y="10" width="14" height="8" rx="2" fill={b.primary} opacity="0.25"/>
        <text x="27" y="17" fontSize="11" fill="#374151" fontFamily="system-ui">Add to Home screen</text>
        <line x1="8" y1="23" x2="122" y2="23" stroke="#E5E7EB" strokeWidth="0.75"/>
        <rect x="8" y="27" width="14" height="8" rx="2" fill="#E5E7EB"/>
        <text x="27" y="33.5" fontSize="11" fill="#9CA3AF" fontFamily="system-ui">Install app</text>
      </svg>
      <span style={lbl}>{t('pwaInstall.visual.lookInMenu')}</span>
    </div>
  );

  if (type === 'copy-link') return (
    <div style={wrap}>
      <svg width="38" height="38" viewBox="0 0 38 38" fill="none">
        <rect x="1" y="1" width="36" height="36" rx="8" stroke="#E5E7EB" strokeWidth="1.5" fill="white"/>
        <rect x="8" y="12" width="15" height="18" rx="3" stroke={b.primary} strokeWidth="1.5" fill="none"/>
        <rect x="15" y="7" width="15" height="18" rx="3" stroke="#9CA3AF" strokeWidth="1.5" fill="white"/>
        <line x1="18" y1="12" x2="27" y2="12" stroke="#E5E7EB" strokeWidth="1"/>
        <line x1="18" y1="16" x2="27" y2="16" stroke="#E5E7EB" strokeWidth="1"/>
      </svg>
      <span style={lbl}>{t('pwaInstall.visual.copyLink')}</span>
    </div>
  );

  if (type === 'open-safari') return (
    <div style={wrap}>
      <svg width="38" height="38" viewBox="0 0 38 38" fill="none">
        <circle cx="19" cy="19" r="17" stroke="#E5E7EB" strokeWidth="1.5" fill="white"/>
        <circle cx="19" cy="19" r="11" stroke="#E5E7EB" strokeWidth="0.75"/>
        <line x1="19" y1="8" x2="19" y2="30" stroke="#D1D5DB" strokeWidth="0.75"/>
        <line x1="8" y1="19" x2="30" y2="19" stroke="#D1D5DB" strokeWidth="0.75"/>
        <polygon points="19,10 23.5,25 19,22 14.5,25" fill={b.primary}/>
        <polygon points="19,28 14.5,13 19,16 23.5,13" fill="#C0C8D1"/>
      </svg>
      <span style={lbl}>{t('pwaInstall.visual.safariCompass')}</span>
    </div>
  );

  if (type === 'open-browser') return (
    <div style={wrap}>
      <svg width="38" height="38" viewBox="0 0 38 38" fill="none">
        <rect x="1" y="1" width="36" height="36" rx="8" stroke="#E5E7EB" strokeWidth="1.5" fill="white"/>
        <rect x="7" y="10" width="24" height="5" rx="2.5" fill="#E5E7EB"/>
        <circle cx="10.5" cy="12.5" r="1.8" fill={b.primary}/>
        <rect x="7" y="18" width="24" height="13" rx="2.5" fill="#F3F4F6"/>
        <circle cx="19" cy="24.5" r="4" stroke={b.primary} strokeWidth="1.5" fill="none"/>
        <path d="M22 22l3 3" stroke={b.primary} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
      <span style={lbl}>{t('pwaInstall.visual.chromeOrEdge')}</span>
    </div>
  );

  if (type === 'mac-menu') return (
    <div style={wrap}>
      <svg width="130" height="22" viewBox="0 0 130 22" fill="none">
        <rect x="0.5" y="0.5" width="129" height="21" rx="3" stroke="#E5E7EB" fill="#F9FAFB"/>
        <rect x="4" y="4" width="20" height="14" rx="2" fill={b.primary} opacity="0.15"/>
        <text x="7" y="14.5" fontSize="10" fill={b.primary} fontFamily="system-ui" fontWeight="bold">File</text>
        <text x="28" y="14.5" fontSize="10" fill="#374151" fontFamily="system-ui">Edit</text>
        <text x="48" y="14.5" fontSize="10" fill="#374151" fontFamily="system-ui">View</text>
        <text x="68" y="14.5" fontSize="10" fill="#374151" fontFamily="system-ui">History</text>
      </svg>
      <span style={lbl}>{t('pwaInstall.visual.fileMenu')}</span>
    </div>
  );

  if (type === 'mac-add-dock') return (
    <div style={wrap}>
      <svg width="130" height="60" viewBox="0 0 130 60" fill="none">
        <rect x="0.5" y="0.5" width="129" height="59" rx="6" stroke="#E5E7EB" fill="white"/>
        <text x="10" y="16" fontSize="10" fill="#9CA3AF" fontFamily="system-ui">New Window    ⌘N</text>
        <line x1="8" y1="20" x2="122" y2="20" stroke="#E5E7EB" strokeWidth="0.75"/>
        <text x="10" y="31" fontSize="10" fill="#9CA3AF" fontFamily="system-ui">Open File…    ⌘O</text>
        <line x1="8" y1="35" x2="122" y2="35" stroke="#E5E7EB" strokeWidth="0.75"/>
        <rect x="8" y="40" width="84" height="15" rx="3.5" fill={b.primary}/>
        <text x="13" y="51.5" fontSize="10" fill="white" fontFamily="system-ui" fontWeight="600">Add to Dock…</text>
      </svg>
      <span style={lbl}>{t('pwaInstall.visual.fileToDock')}</span>
    </div>
  );

  if (type === 'desktop-install-icon') return (
    <div style={wrap}>
      <svg width="130" height="26" viewBox="0 0 130 26" fill="none">
        <rect x="0.5" y="0.5" width="129" height="25" rx="12.5" stroke="#E5E7EB" fill="white"/>
        <text x="12" y="16.5" fontSize="10" fill="#9CA3AF" fontFamily="system-ui">theindieread.com</text>
        <circle cx="113" cy="13" r="8.5" fill={b.primary}/>
        <text x="109" y="17" fontSize="13" fill="white" fontFamily="system-ui" fontWeight="bold">+</text>
      </svg>
      <span style={lbl}>{t('pwaInstall.visual.installIcon')}</span>
    </div>
  );

  if (type === 'desktop-menu') return (
    <div style={wrap}>
      <svg width="26" height="28" viewBox="0 0 26 28" fill="none">
        <rect x="5" y="6" width="16" height="2.5" rx="1.25" fill="#6B7280"/>
        <rect x="5" y="13" width="16" height="2.5" rx="1.25" fill="#6B7280"/>
        <rect x="5" y="20" width="16" height="2.5" rx="1.25" fill="#6B7280"/>
      </svg>
      <span style={lbl}>{t('pwaInstall.visual.browserMenu')}</span>
    </div>
  );

  return <div style={wrap}><span style={lbl}>{t('pwaInstall.visual.followStep')}</span></div>;
}

// ─── InstallGuideSheet — the "follow along" bottom-sheet modal ────────────────

function InstallGuideSheet({ open, onClose, platform }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const b = KIT.brand;

  useEffect(() => { if (open) setStep(0); }, [open]);

  if (!open) return null;

  const guide   = getInstallGuide(platform, t);
  const total   = guide.steps.length;
  const current = guide.steps[step];
  const isFirst = step === 0;
  const isLast  = step === total - 1;

  return (
    // Backdrop — intentionally does NOT close on click so users can follow along
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.52)', backdropFilter: 'blur(3px)' }}
      data-testid="pwa-guide-backdrop"
    >
      <div
        className="w-full max-w-sm overflow-hidden shadow-2xl"
        style={{
          backgroundColor: '#fff',
          color:            b.text,
          borderTopLeftRadius:  20,
          borderTopRightRadius: 20,
          borderBottomLeftRadius:  0,
          borderBottomRightRadius: 0,
        }}
        /* sm and above → real modal rounding */
        onClick={e => e.stopPropagation()}
        data-testid="pwa-guide-sheet"
      >
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div
          className="flex items-start justify-between px-5 pt-5 pb-3"
          style={{ borderBottom: `1px solid ${b.hint_border}` }}
        >
          <div className="pr-2 flex-1 min-w-0">
            <p className="font-bold text-[17px] leading-snug truncate">{guide.title}</p>
            <p className="mt-0.5 text-[11px]" style={{ color: b.muted }}>
              {guide.browserName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('pwaInstall.guide.closeAria')}
            data-testid="pwa-guide-close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition hover:bg-black/5"
            style={{ color: b.muted }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Progress bar ──────────────────────────────────────────────── */}
        <div className="flex gap-1.5 px-5 pt-4">
          {guide.steps.map((_, i) => (
            <div
              key={i}
              className="h-1 flex-1 rounded-full transition-all duration-300"
              style={{ backgroundColor: i <= step ? b.primary : b.hint_border }}
            />
          ))}
        </div>
        <p className="px-5 pt-1.5 text-[10.5px]" style={{ color: b.muted }}>
          {t('pwaInstall.guide.stepOf', { step: step + 1, total })}
        </p>

        {/* ── Step content ──────────────────────────────────────────────── */}
        <div className="px-5 pt-3 pb-2">
          <div className="flex items-center gap-3 mb-3">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[13px] font-bold"
              style={{ backgroundColor: b.primary, color: b.on_primary }}
            >
              {step + 1}
            </span>
            <h3 className="font-bold text-[15px] leading-snug">{current.title}</h3>
          </div>

          <VisualHint type={current.visual} t={t} />

          <p className="mt-3 text-[13px] leading-relaxed" style={{ color: '#4B5563' }}>
            {current.desc}
          </p>

          {current.action && (
            <button
              type="button"
              onClick={current.action.fn}
              data-testid="pwa-guide-copy-btn"
              className="mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold transition hover:brightness-105 active:scale-95"
              style={{
                backgroundColor: b.surface,
                border:          `1px solid ${b.border}`,
                color:           b.text,
              }}
            >
              <Clipboard className="h-3.5 w-3.5" />
              {current.action.label}
            </button>
          )}
        </div>

        {/* ── Hint note (first step only) ───────────────────────────────── */}
        {isFirst && guide.hint && (
          <div
            className="mx-5 mb-1 rounded-lg px-3 py-2 text-[11px] leading-relaxed"
            style={{ backgroundColor: b.bg_step, color: b.muted }}
          >
            {guide.hint}
          </div>
        )}

        {/* ── Navigation ────────────────────────────────────────────────── */}
        <div className="flex gap-2 px-5 pb-7 pt-3">
          {!isFirst && (
            <button
              type="button"
              onClick={() => setStep(s => s - 1)}
              data-testid="pwa-guide-back"
              className="h-11 flex-1 rounded-full text-[13px] font-semibold transition hover:bg-black/5"
              style={{ border: `1.5px solid ${b.border}`, color: b.text, background: 'none' }}
            >
              {t('pwaInstall.guide.back')}
            </button>
          )}
          {!isLast ? (
            <button
              type="button"
              onClick={() => setStep(s => s + 1)}
              data-testid="pwa-guide-next"
              className="h-11 flex-1 rounded-full text-[14px] font-bold transition hover:brightness-105 active:scale-95"
              style={{ backgroundColor: b.primary, color: b.on_primary }}
            >
              {t('pwaInstall.guide.nextStep')}
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              data-testid="pwa-guide-done"
              className="h-11 flex-1 rounded-full text-[14px] font-bold transition hover:brightness-105 active:scale-95"
              style={{ backgroundColor: b.primary, color: b.on_primary }}
            >
              {t('pwaInstall.guide.done')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Supporting dialogs (unchanged) ──────────────────────────────────────────

function Overlay({ onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 sm:p-6"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
      onClick={onClose}
      data-testid="pwa-overlay"
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6 shadow-2xl flex flex-col gap-4"
        style={{ backgroundColor: '#fff', color: KIT.brand.text }}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function InstalledDialog({ open, onClose }) {
  const { t } = useTranslation();
  const b = KIT.brand;
  if (!open) return null;
  return (
    <Overlay onClose={onClose}>
      <div className="flex flex-col items-center gap-3 text-center">
        <img src={KIT.icons.large} alt="" className="h-16 w-16 rounded-2xl shadow" />
        <p className="text-[20px] font-extrabold">{t('pwaInstall.installed.title')}</p>
        <p className="text-[14px] leading-relaxed" style={{ color: b.muted }}>
          {t('pwaInstall.installed.body')}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="h-12 w-full rounded-3xl text-[15px] font-bold"
          style={{ backgroundColor: b.primary, color: b.on_primary }}
          data-testid="pwa-installed-close"
        >
          {t('pwaInstall.installed.done')}
        </button>
      </div>
    </Overlay>
  );
}

function WhyDialog({ open, onClose }) {
  const { t } = useTranslation();
  const b = KIT.brand;
  if (!open) return null;
  return (
    <Overlay onClose={onClose}>
      <p className="text-[18px] font-extrabold">{t('pwaInstall.why.title')}</p>
      <p className="text-[14px] leading-relaxed">{t('pwaInstall.why.para1')}</p>
      <p className="text-[14px] leading-relaxed">{t('pwaInstall.why.para2')}</p>
      <button
        type="button"
        onClick={onClose}
        className="h-11 w-full rounded-3xl text-[14px] font-bold"
        style={{ backgroundColor: b.primary, color: b.on_primary }}
        data-testid="pwa-why-close"
      >
        {t('pwaInstall.guide.gotIt')}
      </button>
    </Overlay>
  );
}

function PrivacyDialog({ open, onClose }) {
  const { t } = useTranslation();
  const b = KIT.brand;
  if (!open) return null;
  return (
    <Overlay onClose={onClose}>
      <p className="text-[18px] font-extrabold">{t('pwaInstall.privacy.title')}</p>
      <p className="text-[14px] leading-relaxed">{t('pwaInstall.privacy.body')}</p>
      <button
        type="button"
        onClick={onClose}
        className="h-11 w-full rounded-3xl text-[14px] font-bold"
        style={{ backgroundColor: b.primary, color: b.on_primary }}
      >
        {t('pwaInstall.guide.gotIt')}
      </button>
    </Overlay>
  );
}

// ─── Named exports for reuse in other nav / page components ─────────────────
export { InstallGuideSheet, detectPlatform };

// ─── Main card ───────────────────────────────────────────────────────────────

export default function PwaInstallCard({ className = '' }) {
  const { t } = useTranslation();
  const [visible,       setVisible]       = useState(false);
  const [platform,      setPlatform]      = useState({});
  const [showGuide,     setShowGuide]     = useState(false);
  const [showInstalled, setShowInstalled] = useState(false);
  const [showWhy,       setShowWhy]       = useState(false);
  const [showPrivacy,   setShowPrivacy]   = useState(false);
  const deferredPrompt = useRef(null);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;
    const p = detectPlatform();
    setPlatform(p);
    setVisible(true);

    // Only capture beforeinstallprompt when the user is already inside the
    // Reader scope (/reader/*).  On main-site pages the root manifest now has
    // scope "/" (Writer App), so deferredPrompt would offer to install the
    // Writer App — not the Reader App this card promotes.  We let the browser
    // surface its own native Writer-App install affordance un-intercepted and
    // always guide the user through the manual Reader install steps instead.
    const isReaderContext = window.location.pathname.startsWith('/reader');
    if (!isReaderContext) return;

    const handler = (e) => { e.preventDefault(); deferredPrompt.current = e; };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => {
      setShowInstalled(true);
      saveDismiss();
      setVisible(false);
    });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = () => {
    // triggerReaderInstall routes traffic correctly:
    //   • /reader/* page + prompt ready → native install
    //   • Main-site + Chrome-family    → relay to /reader/?pwa_install=1
    //   • iOS / Firefox / other        → show InstallGuideSheet
    triggerReaderInstall(platform, () => setShowGuide(true));
  };

  const handleDismiss = () => { saveDismiss(); setVisible(false); };

  if (!visible) return null;

  const { brand: b, copy: c } = KIT;
  const icon = platform.ios ? KIT.icons.ios : KIT.icons.android;

  return (
    <>
      <div
        data-testid="pwa-install-card"
        className={`inline-flex flex-col gap-2 rounded-xl border p-2.5 ${className}`}
        style={{
          backgroundColor: b.surface,
          borderColor:     b.border,
          color:           b.text,
          fontFamily:      "'DM Sans', system-ui, sans-serif",
          maxWidth:        '220px',
        }}
      >
        <div className="flex items-start gap-2">
          <img
            src={icon}
            alt="Crown & Rose"
            className="h-8 w-8 shrink-0 shadow-sm"
            style={{ borderRadius: platform.ios ? '8px' : '10px' }}
            data-testid="pwa-card-icon"
          />
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-bold leading-tight">{t('pwaInstall.card.title')}</p>
            <p className="mt-0.5 text-[11px] leading-tight" style={{ color: b.muted }}>{t('pwaInstall.card.subtitle')}</p>
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={handleDismiss}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-black/5"
            style={{ color: b.muted }}
            data-testid="pwa-card-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <button
          type="button"
          onClick={handleInstallClick}
          data-testid="pwa-card-install-btn"
          className="inline-flex w-fit items-center justify-center rounded-full px-3 py-1 text-[11px] font-bold transition hover:brightness-110 active:scale-95"
          style={{ backgroundColor: b.primary, color: b.on_primary }}
        >
          {t('pwaInstall.card.installBtn')}
        </button>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowWhy(true)}
            className="text-left text-[10px] font-bold underline"
            style={{ background: 'none', border: 0, padding: 0, color: b.primary }}
            data-testid="pwa-card-why-btn"
          >
            {t('pwaInstall.card.whyBtn')}
          </button>
          <button
            type="button"
            onClick={() => setShowPrivacy(true)}
            className="text-left text-[10px]"
            style={{ background: 'none', border: 0, padding: 0, color: b.muted }}
            data-testid="pwa-card-privacy-btn"
          >
            {t('pwaInstall.privacy.btn')}
          </button>
        </div>
      </div>

      <InstallGuideSheet open={showGuide}     onClose={() => setShowGuide(false)}     platform={platform} />
      <InstalledDialog   open={showInstalled} onClose={() => setShowInstalled(false)} />
      <WhyDialog         open={showWhy}       onClose={() => setShowWhy(false)}        />
      <PrivacyDialog     open={showPrivacy}   onClose={() => setShowPrivacy(false)}    />
    </>
  );
}
